import { defaultTopicLanguage, normalizeTopicLanguage } from "./languages";

export type SearchScope = "full" | "title";

export interface SearchTerm {
  term: string;
  proximity?: number;
}

export interface PackTopic {
  topicId: string;
  title: string;
  language: string;
  searchScope: SearchScope;
  terms: SearchTerm[];
  publisherFilterIds?: string[];
}

export interface IndustryPack {
  packId: string;
  packVersion: string;
  industry: string;
  summary: string;
  suggestedSourceIds: string[];
  topics: PackTopic[];
}

export interface SourceEntry {
  sourceId: string;
  name: string;
  shortName: string;
  country: string;
  kind: "regulator" | "industry-body";
  logoPath: string;
  brandColor: string;
  /**
   * RI publisher GUID from shared `ri-publishers.json` (empty string when status is missing).
   * Sole GUID source for LowTouch; Automation must sync from the same master.
   */
  riPublisherId: string;
}

export type DigestFrequency = "daily" | "weekly" | "biweekly";

export type PersonaId =
  | "in-house"
  | "advisor"
  | "vendor"
  | "other";

export interface CustomTopicDraft {
  title: string;
  language: string;
  terms: SearchTerm[];
}

/** Normalize legacy `{ title, term }` payloads and trim empty keyword rows. */
export function normalizeCustomTopic(raw: unknown): CustomTopicDraft | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const title = typeof o.title === "string" ? o.title : "";
  const language = normalizeTopicLanguage(
    typeof o.language === "string" && o.language.trim() ? o.language : defaultTopicLanguage,
  );
  let terms: SearchTerm[] = [];
  if (Array.isArray(o.terms)) {
    terms = o.terms
      .map((row) => {
        if (!row || typeof row !== "object") return null;
        const t = row as Record<string, unknown>;
        const term = typeof t.term === "string" ? t.term : "";
        const proximity =
          typeof t.proximity === "number" && Number.isFinite(t.proximity) ? t.proximity : undefined;
        return proximity != null ? { term, proximity } : { term };
      })
      .filter((t): t is SearchTerm => t != null);
  } else if (typeof o.term === "string") {
    terms = o.term.trim() ? [{ term: o.term }] : [];
  }
  return { title, language, terms };
}

export function trimCustomTopic(draft: CustomTopicDraft): CustomTopicDraft {
  return {
    title: draft.title.trim(),
    language: normalizeTopicLanguage(draft.language),
    terms: draft.terms
      .map((t) => {
        const term = t.term.trim();
        const proximity =
          typeof t.proximity === "number" && t.proximity >= 1 && t.proximity <= 10000
            ? t.proximity
            : undefined;
        return proximity != null ? { term, proximity } : { term };
      })
      .filter((t) => t.term.length > 0),
  };
}

export function seedCustomTopic(packTerms?: SearchTerm[], language = defaultTopicLanguage): CustomTopicDraft {
  const seed = (packTerms ?? []).slice(0, 2);
  return {
    title: "",
    language,
    terms:
      seed.length > 0
        ? seed.map((t) =>
            t.proximity != null ? { term: t.term, proximity: t.proximity } : { term: t.term },
          )
        : [
            { term: "customer due diligence", proximity: 8 },
            { term: "money laundering", proximity: 5 },
          ],
  };
}

export interface WizardState {
  packId: string;
  packVersion: string;
  topicIds: string[];
  includeAll: boolean;
  customTopic: CustomTopicDraft | null;
  publisherIds: string[];
  requestedSource: string;
  frequency: DigestFrequency;
  sendTime: string;
  timezone: string;
  persona: PersonaId | "";
  email: string;
}

export const emptyWizardState = (packId = ""): WizardState => ({
  packId,
  packVersion: "",
  topicIds: [],
  includeAll: false,
  customTopic: null,
  publisherIds: [],
  requestedSource: "",
  frequency: "weekly",
  sendTime: "08:00",
  timezone: "Europe/Copenhagen",
  persona: "",
  email: "",
});
